import React from 'react';
import css from './SectionThreeColumns.module.css';
import { getStoreImageUrl } from '../../util/sellerImages';

const StarIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.08611 2.05727C7.43796 1.26523 8.56204 1.26523 8.91389 2.05728L9.97053 4.43591C10.1158 4.76296 10.4248 4.98744 10.7807 5.02455L13.3695 5.29444C14.2315 5.38431 14.5788 6.45338 13.9343 7.03276L11.9986 8.77272C11.7324 9.01196 11.6144 9.37518 11.6891 9.72516L12.2324 12.2706C12.4133 13.1182 11.5039 13.7789 10.7537 13.3449L8.50073 12.0417C8.19096 11.8625 7.80904 11.8625 7.49927 12.0417L5.24631 13.3449C4.49611 13.7789 3.5867 13.1182 3.7676 12.2706L4.31089 9.72516C4.38559 9.37518 4.26757 9.01196 4.00142 8.77272L2.06573 7.03275C1.42117 6.45338 1.76854 5.38431 2.63054 5.29444L5.21928 5.02455C5.57521 4.98744 5.88419 4.76296 6.02947 4.43591L7.08611 2.05727Z"
      fill={filled ? 'var(--primary-camo, #6E712E)' : '#D9D9D9'}
    />
  </svg>
);

const ChevronIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_5000_50745)">
      <mask
        id="mask0_5000_50745"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="40"
        height="40"
      >
        <rect width="40" height="40" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_5000_50745)">
        <path
          d="M21.5998 19.7008L13.7998 11.8008L14.7998 10.8008L23.6998 19.7008L14.7998 28.6008L13.7998 27.6008L21.5998 19.8008V19.7008Z"
          fill="#1E1E1E"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_5000_50745">
        <rect width="40" height="40" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// Same parser used in CompactListingCard — "123 Main St, Dallas, TX 75201, United States" → "Dallas, TX"
const formatCityState = address => {
  if (!address) return '';
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  const filtered = parts.filter(p => !/^(United States|USA|US|Canada|Mexico)$/i.test(p));
  const list = filtered.length > 0 ? filtered : parts;
  if (list.length >= 2) {
    const city = list[list.length - 2];
    const stateZip = list[list.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
    return stateZip ? `${city}, ${stateZip}` : city;
  }
  return list[0]?.replace(/\s*\d{5}(-\d{4})?$/, '').trim() || address;
};

const SectionThreeColumns = props => {
  const {
    publicData = {},
    author,
    reviews = [],
    compact = false,
    isIframe = false,
    showLocation = true,
  } = props;

  const authorPublicData = author?.attributes?.profile?.publicData || {};
  const authorDisplayName = author?.attributes?.profile?.displayName || '';
  const businessName = authorPublicData.businessName || authorDisplayName;

  const logoUrl = getStoreImageUrl(author) || '/static/footer-logo.png';

  // City, State — same source priority as CompactListingCard
  const rawLocation =
    publicData.dealerLocation ||
    authorPublicData.storedAddress?.place_name ||
    publicData.location?.address ||
    '';
  const location = formatCityState(rawLocation);

  // Average star rating (rounded to nearest whole star)
  const avgRating =
    reviews.length > 0
      ? Math.round(
          reviews.reduce((sum, r) => sum + (r.attributes?.rating || 0), 0) /
            reviews.length
        )
      : 0;

  const authorId = author?.id?.uuid;
  const storeUrl = authorId ? `/store/${authorId}/detail` : null;

  const CardWrapper = isIframe ? 'div' : 'a';
  const wrapperProps = isIframe
    ? { style: { cursor: 'default' } }
    : { href: storeUrl, style: { textDecoration: 'none' } };

  return (
    <CardWrapper className={compact ? css.cardCompact : css.card} {...wrapperProps}>
      {/* Left: logo | location + stars */}
      <div className={css.leftContainer}>
        {logoUrl ? (
          <img className={css.dealerLogo} src={logoUrl} alt={businessName} />
        ) : (
          <span className={css.dealerName}>{businessName}</span>
        )}

        <div className={css.locationReviewsGroup}>
          <span className={css.dealerNameMobile}>{businessName}</span>
          <hr className={css.dealerDivider} />
          {showLocation && location ? <p className={css.location}>{location}</p> : null}
          <div className={css.stars}>
            {Array.from({ length: 5 }, (_, i) => (
              <StarIcon key={i} filled={i < avgRating} />
            ))}
          </div>
        </div>
      </div>

      {/* Right: chevron — hidden in compact mode */}
      {!compact && (
        <div className={css.rightContainer}>
          <ChevronIcon />
        </div>
      )}
    </CardWrapper>
  );
};

export default SectionThreeColumns;
